import {
  Box,
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useShowSnackbar } from 'components/main/SnackbarProvider';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { addLesson } from 'services/LessonService';
import UploadForm from './components/UploadForm';
import { EMPTY_FORM, NUMBER_OF_STEPS, STEPS } from './constants';
import { LessonForm, Props } from './types';
import {
  goToStep as generateGoToStep,
  isStepErrored,
  nextStep as generateNextStep,
  previousStep as generatePreviousStep,
} from './utils';
import { getLessonSchema } from './validation';

const useStyles = makeStyles({
  stepButton: {
    textTransform: 'none',
  },
});

const SubmitForm: React.FC<Props> = ({ defaultStep = 0 }) => {
  const history = useHistory();
  const showSnackbar = useShowSnackbar();

  const [activeStep, setActiveStep] = useState(defaultStep);
  const [lessonFile, setLessonFile] = useState<File>();

  function handleFileUploadChange(newFile: File) {
    if (!lessonFile || newFile !== lessonFile) {
      setLessonFile(newFile);
    }
  }

  const classes = useStyles();

  return (
    <Formik<LessonForm>
      initialValues={EMPTY_FORM}
      validationSchema={getLessonSchema(lessonFile)}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await addLesson(values, lessonFile as File);
          history.push('/');
          showSnackbar(
            <Alert severity="success">
              Lesson submitted successfully! Your lesson will be reviewed shortly.
            </Alert>,
          );
          window.scrollTo(0, 0);
        } catch (error) {
          console.error('Error submitting lesson', error);

          showSnackbar(
            <Alert severity="error">
              Error ocurred while submitting lesson. Please contact support.
            </Alert>,
          );
        }
        setSubmitting(false);
      }}
    >
      {({ errors, touched, values, setFieldTouched, isSubmitting, validateForm }) => {
        // When the selected file changes, validate the form to run file validation checks
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          validateForm();
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lessonFile]);

        const previousStep = generatePreviousStep(
          activeStep,
          setActiveStep,
          values,
          setFieldTouched,
        );
        const nextStep = generateNextStep(activeStep, setActiveStep, values, setFieldTouched);
        const goToStep = (step: number) =>
          generateGoToStep(activeStep, step, setActiveStep, values, setFieldTouched);
        const isLastPage = activeStep === NUMBER_OF_STEPS - 1;

        return (
          <Grid direction="column" spacing={2} container>
            <Grid item>
              <Typography>
                Thank you for contributing to our lesson database. Please feel out the following
                information. Once you submit this form, someone from our team will review it and be
                in touch with you.
              </Typography>
            </Grid>
            <Grid item>
              <Stepper activeStep={activeStep} orientation="vertical">
                {STEPS.map(({ label }, index) => {
                  function handleClick() {
                    goToStep(index);
                  }

                  const CurrentStepContent = STEPS[index].Component;

                  return (
                    <Step key={index}>
                      <StepLabel error={isStepErrored(label, errors, touched)}>
                        <Button classes={{ root: classes.stepButton }} onClick={handleClick}>
                          {label}
                        </Button>
                      </StepLabel>
                      <StepContent>
                        <Form>
                          {CurrentStepContent === UploadForm ? (
                            <CurrentStepContent
                              lessonFile={lessonFile}
                              handleFileUploadChange={handleFileUploadChange}
                            />
                          ) : (
                            <CurrentStepContent />
                          )}
                          <ButtonContainer>
                            <Button onClick={previousStep} disabled={activeStep === 0}>
                              Previous
                            </Button>
                            {/* If you convert this to a ternary, for some reason clicking Next on the last page
                will submit the whole form */}
                            {isLastPage &&
                              (isSubmitting ? (
                                <Button color="primary" variant="contained">
                                  <CircularProgress size={16} color="secondary" />
                                  <Box ml={1}>Saving...</Box>
                                </Button>
                              ) : (
                                <Button color="primary" variant="contained" type="submit">
                                  Finish
                                </Button>
                              ))}
                            {!isLastPage && (
                              <Button color="primary" variant="contained" onClick={nextStep}>
                                Next
                              </Button>
                            )}
                          </ButtonContainer>
                        </Form>
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </Grid>
          </Grid>
        );
      }}
    </Formik>
  );
};

// Styled components
const ButtonContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(),
}));

export default SubmitForm;
